import { useViktorRunState } from '../../../store/viktorRun';
import { ThinkingIndicator } from './ThinkingIndicator';

/**
 * What each phase is called, in the words the thread uses.
 *
 * The backend sends the phase and nothing else; the sentences are ours. An
 * unknown one falls back to the first line rather than to silence, so a phase
 * added later still says something true. Written like the merchant bot's own
 * lines (`ASSISTANT_THINKING_LABEL`) - the same verbs, the same ellipsis - as
 * they are the same indicator on two surfaces.
 */
const PHASE_LABEL: Record<string, string> = {
  reading: 'Reading your question...',
  checkingData: 'Checking our data...',
};

/**
 * "Checking our data..." inside the bubble Viktor is writing into.
 *
 * Reads the phase itself instead of taking it as a prop: it changes several
 * times inside one answer, and a prop would re-render every message in the
 * thread to move three words at the bottom of it. Only rendered while a run is
 * in flight, so nothing subscribes once the answer is stored.
 *
 * Nothing is said while he WRITES. The phase alternates between writing and
 * checking as he works, and a line reading "Writing..." under text that is
 * visibly being written is noise; when it is missing the words below have
 * nothing to add to the words above. Which is also why the dots stay
 * unconditionally in the empty bubble (`standalone`): before the first
 * character they are the only thing on it, phase or no phase.
 */
export const ViktorStatus = ({ standalone }: { standalone?: boolean }) => {
  const phase = useViktorRunState((state) => state.phase);

  const label = phase
    ? phase === 'writing' || phase === 'done'
      ? null
      : PHASE_LABEL[phase] ?? PHASE_LABEL.reading
    : null;

  return (
    <ThinkingIndicator
      label={label}
      tone="gradient"
      alwaysShow={standalone}
      inline={!standalone}
    />
  );
};
